// components/SalarySlipViewModal.jsx
import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  Chip,
  Avatar,
  Button,
  IconButton,
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Zoom from "@mui/material/Zoom";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import EmailIcon from "@mui/icons-material/Email";
import PrintIcon from "@mui/icons-material/Print";
import BusinessIcon from "@mui/icons-material/Business";

// Standard professional colors
const colors = {
  primary: "#1E3A5F",      // Standard corporate navy
  secondary: "#2C3E50",     // Dark slate
  border: "#E0E0E0",        // Light gray border
  background: "#FFFFFF",     // White
  lightBg: "#F5F5F5",       // Light gray background
  text: {
    primary: "#333333",      // Dark gray for main text
    secondary: "#666666",    // Medium gray for labels
  }
};

// Standard salary slip layout
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 8,
    boxShadow: '0 5px 20px rgba(0,0,0,0.1)',
    maxWidth: '900px',
    margin: theme.spacing(2),
  }
}));

const HeaderBox = styled(Box)({
  padding: '20px 24px',
  borderBottom: `2px solid ${colors.primary}`,
  backgroundColor: colors.background,
});

const CompanyName = styled(Typography)({
  fontWeight: 700,
  color: colors.primary,
  fontSize: '1.5rem',
  letterSpacing: '0.5px',
});

const SectionTitle = styled(Typography)({
  fontSize: '1rem',
  fontWeight: 600,
  color: colors.primary,
  marginBottom: '16px',
  paddingBottom: '6px',
  borderBottom: `1px solid ${colors.border}`,
  textTransform: 'uppercase',
});

const LabelText = styled(Typography)({
  fontSize: '0.75rem',
  color: colors.text.secondary,
  marginBottom: '2px',
  fontWeight: 500,
});

const ValueText = styled(Typography)({
  fontSize: '0.9rem',
  color: colors.text.primary,
  fontWeight: 500,
});

const SalarySlipViewModal = ({ open, onClose, slip, onDownload, onEmail, companyLogo }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!slip) return null;

  // Calculate totals
  const earningsTotal = slip.earnings_breakdown?.reduce((sum, item) => sum + (item.amount || 0), 0) || 0;
  const deductionsTotal = slip.deductions_breakdown?.reduce((sum, item) => sum + (item.amount || 0), 0) || 0;

  // Format currency in standard Indian format
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount).replace('₹', '₹ ');
  };

  // Get month name
  const getMonthName = (month) => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                   'July', 'August', 'September', 'October', 'November', 'December'];
    return months[parseInt(month) - 1] || '';
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth TransitionComponent={Zoom}>
      
      {/* Standard Salary Slip Header */}
      <HeaderBox>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={8}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar 
                src={companyLogo}
                sx={{ width: 60, height: 60, bgcolor: colors.primary }}
                variant="square"
              >
                <BusinessIcon sx={{ fontSize: 30, color: 'white' }} />
              </Avatar>
              <Box>
                <CompanyName>BiggBrains Solutions Pvt Ltd</CompanyName>
                <Typography variant="body2" sx={{ color: colors.text.secondary, mt: 0.5 }}>
                  501, Business Hub, Andheri East, Mumbai - 400069
                </Typography>
                <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                  Tel: +91 22 4567 8900 | Email: finance@biggbrains.com
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: colors.primary }}>
                SALARY SLIP
              </Typography>
              <Typography variant="body2" sx={{ color: colors.text.secondary, mt: 0.5 }}>
                For the month of {getMonthName(slip.month)} {slip.year}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </HeaderBox>

      <DialogContent sx={{ p: 0 }}>

        {/* Employee Details Section - Standard Format */}
        <Box sx={{ p: 3 }}>
          <SectionTitle>EMPLOYEE DETAILS</SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <LabelText>Employee Name</LabelText>
              <ValueText sx={{ fontWeight: 600 }}>
                {slip.employee?.name || slip.employee_name || ''}
              </ValueText>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <LabelText>Employee ID</LabelText>
              <ValueText>{slip.employee?.emp_id || `BB${slip.user_id}`}</ValueText>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <LabelText>Designation</LabelText>
              <ValueText>{slip.employee?.role || 'Software Engineer'}</ValueText>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <LabelText>Department</LabelText>
              <ValueText>{slip.employee?.department || 'Technology'}</ValueText>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <LabelText>Date of Joining</LabelText>
              <ValueText>01 Jan 2023</ValueText>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <LabelText>PAN Number</LabelText>
              <ValueText>ABCDE1234F</ValueText>
            </Grid>
            <Grid item xs={12} sm={6}>
              <LabelText>Bank Account Number</LabelText>
              <ValueText>XXXX XXXX XXXX 1234</ValueText>
            </Grid>
            <Grid item xs={12} sm={6}>
              <LabelText>UAN Number</LabelText>
              <ValueText>123456789012</ValueText>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ borderColor: colors.border }} />

        {/* Earnings & Deductions Section - Standard Table Format */}
        <Box sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <SectionTitle>EARNINGS</SectionTitle>
              <TableContainer component={Paper} variant="outlined" sx={{ borderColor: colors.border }}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: colors.lightBg }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Particulars</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Amount (₹)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {slip.earnings_breakdown?.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{item.component_name}</TableCell>
                        <TableCell align="right">{formatCurrency(item.amount)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: colors.lightBg }}>
                      <TableCell><Typography fontWeight={700}>Gross Earnings</Typography></TableCell>
                      <TableCell align="right"><Typography fontWeight={700}>{formatCurrency(earningsTotal)}</Typography></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            <Grid item xs={12} md={6}>
              <SectionTitle>DEDUCTIONS</SectionTitle>
              <TableContainer component={Paper} variant="outlined" sx={{ borderColor: colors.border }}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: colors.lightBg }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Particulars</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Amount (₹)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {slip.deductions_breakdown?.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{item.component_name}</TableCell>
                        <TableCell align="right">{formatCurrency(item.amount)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: colors.lightBg }}>
                      <TableCell><Typography fontWeight={700}>Total Deductions</Typography></TableCell>
                      <TableCell align="right"><Typography fontWeight={700}>{formatCurrency(deductionsTotal)}</Typography></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </Box>

        {/* Salary Summary - Standard Format */}
        <Box sx={{ px: 3, pb: 2 }}>
          <Paper sx={{ p: 2, bgcolor: colors.lightBg, border: `1px solid ${colors.border}` }}>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <LabelText>Gross Salary</LabelText>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {formatCurrency(slip.gross_salary)}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <LabelText>Total Deductions</LabelText>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {formatCurrency(slip.total_deduction)}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <LabelText>Net Salary Paid</LabelText>
                <Typography variant="h6" sx={{ fontWeight: 700, color: colors.primary }}>
                  {formatCurrency(slip.net_salary)}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <LabelText>Payment Mode</LabelText>
                <Typography variant="body1">Bank Transfer</Typography>
              </Grid>
            </Grid>
          </Paper>
        </Box>

        {/* Amount in Words - Standard */}
        <Box sx={{ px: 3, pb: 2 }}>
          <Typography variant="body2" sx={{ color: colors.text.secondary }}>
            Amount in words: <strong>Rupees {convertNumberToWords(slip.net_salary)} Only</strong>
          </Typography>
        </Box>

        {/* Declaration and Signatures - Standard */}
        <Box sx={{ p: 3, borderTop: `1px solid ${colors.border}` }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Typography variant="body2" sx={{ color: colors.text.secondary, fontStyle: 'italic' }}>
                This is a computer generated salary slip and does not require signature.
                This document is confidential and intended only for the employee.
              </Typography>
              <Typography variant="caption" sx={{ color: colors.text.secondary, display: 'block', mt: 1 }}>
                Generated on: {formatDate(slip.created_at)} • Document ID: SLIP/{slip.year}/{String(slip.month).padStart(2,'0')}/{String(slip.id).padStart(4,'0')}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="body2" sx={{ color: colors.text.primary, fontWeight: 500 }}>
                  For BiggBrains Solutions
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                    Authorized Signatory
                  </Typography>
                  <Divider sx={{ mt: 0.5, width: '150px', ml: 'auto', borderColor: colors.primary }} />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      {/* Action Buttons */}
      <DialogActions sx={{ p: 2.5, borderTop: `1px solid ${colors.border}`, bgcolor: colors.lightBg }}>
        <Button onClick={onClose} variant="outlined" size={isMobile ? "small" : "medium"}>
          Close
        </Button>
        <Button 
          variant="contained" 
          startIcon={<PrintIcon />}
          onClick={() => window.print()}
          size={isMobile ? "small" : "medium"}
          sx={{ bgcolor: colors.secondary }}
        >
          Print
        </Button>
        <Button 
          variant="contained" 
          startIcon={<PictureAsPdfIcon />}
          onClick={() => onDownload(slip)}
          size={isMobile ? "small" : "medium"}
          sx={{ bgcolor: colors.primary }}
        >
          PDF
        </Button>
        <Button 
          variant="contained" 
          startIcon={<EmailIcon />}
          onClick={() => onEmail(slip)}
          size={isMobile ? "small" : "medium"}
          sx={{ bgcolor: colors.primary }}
        >
          Email
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

// Standard number to words converter for Indian currency
const convertNumberToWords = (num) => {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  
  if (num === 0) return 'Zero';
  
  const numToWords = (n) => {
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + numToWords(n % 100) : '');
    if (n < 100000) return numToWords(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + numToWords(n % 1000) : '');
    if (n < 10000000) return numToWords(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + numToWords(n % 100000) : '');
    return numToWords(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + numToWords(n % 10000000) : '');
  };
  
  return numToWords(Math.floor(num));
};

export default SalarySlipViewModal;